import { deepmerge } from 'deepmerge-ts'
import { produce } from 'immer'
import {
  curry as _curry,
  flatten as _flatten,
  flattenDeep as _flattenDeep,
  flow as _flow,
  isEmpty as _isEmpty,
  last as _last,
} from 'lodash/fp'

import { ButtonVariant, MessageType } from '@flowx/angular-ui-toolkit'
import { DataModelAttributeType } from '@flowx/designer/modules/core/dictionary/data-model.dictionary'
import { Platform } from '@flowx/designer/modules/theme/model/platform.model'
import {
  TemplateConfigEntity,
  allowedFormComponents,
} from '@flowx/designer/modules/ui-designer/services/templateConfig.entity'
import { UiDesignerService } from '@flowx/designer/modules/ui-designer/services/ui-designer.service'
import {
  ComponentIdentifierEnum,
  DisplayOptionsInterface,
  FormFieldsEnum,
  NomenclatorValue,
  TemplateConfigInterface,
  TemplateConfigSaveStatus,
  TemplateConfigValidationStatus,
} from '@flowx/shared/interfaces'
import { DimensionTypes, GridPositionType, WidthType } from '@flowx/ui-theme'

import {
  AiDesignerContext,
  DataModelDraft,
  DataModelDraftItem,
  DataModelDraftUpdateItem,
  InputType,
} from '../../../common/ai.dictionary'

export type BulkUpdatePayload = Omit<
  TemplateConfigInterface,
  'id' | 'flowxUuid' | 'templateConfig'
> & {
  templateConfigWithData: BulkUpdatePayload[]
  dataModelProperty: {
    name: string
    description: string
    type: DataModelAttributeType
    exampleValue: string
    useInReporting: boolean
    isSensitive: boolean
    isArray: boolean
  }
}

export type AiTemplateConfig = {
  interface: AiUiInterface
  sectionsDetails: AiUiTemplateSectionDetails[]
}

export type AiTemplateConfigNew = {
  components: AiUiTemplateElementNew[]
}

export type AiUiTemplateElementNew = {
  id: number
  pId: number
  ci: string
  lt: string
  dir: string
  g: number
  cl: number
  al: string
  js: string
  wp: string
  gaph: number
  gapv: number
  cspan: number
  pd: string
  mg: string
  wt: string
  ht: string
  chaxis: string
  cvaxis: string
  atp: string
  lb: string
  ex: string
  st: string
  k: string
  w: number
  h: number
  ds: string
  dt: string
  bgCol: string
  fgCol: string
}

export type AiLocationUI = {
  ci: number
  index: number
  prompt: string
}

export type AiUiContainerGridConfig = {
  clno: number
  rwno?: number
}

export type AiUiInterface = {
  it: string
  ty: 'WB'
  al: 'center' | 'start'
  dir: 'column'
  ds: string
  sc: AiUiTemplateSection[]
} & AiUiContainerGridConfig

export type AiUiTemplateSectionDetails = {
  sc: AiUiTemplateSection
  rs: AiUiTemplateRegion[]
}

export type AiUiTemplateSection = {
  id?: number
  nm: string // name
  k: string // key
  ds: string // description
  gp: AiUiTemplateGridPosition // grid position
  sz: AiUiTemplateSectionSize // size
} & AiUiContainerGridConfig

export type AiUiTemplateRegion = {
  tp: AiRegionType
  clno: number // column number
  rspan: number // row span
  cs: AiUiTemplateElement[]
} & AiUiContainerGridConfig

export type AiRegionType = 'CN' | 'CD' | 'FM'

const AiRegionTypeToComponentIdentifierEnum: Record<AiRegionType, ComponentIdentifierEnum> = {
  CN: ComponentIdentifierEnum.CONTAINER,
  CD: ComponentIdentifierEnum.CARD,
  FM: ComponentIdentifierEnum.FORM,
}

export enum AiComponentDataType {
  N = DataModelAttributeType.INTEGER,
  S = DataModelAttributeType.STRING,
  B = DataModelAttributeType.BOOLEAN,
  D = DataModelAttributeType.DATE_TIME,
  C = DataModelAttributeType.CURRENCY,
  O = DataModelAttributeType.OBJECT,
  NONE = '',
}

export const AiInputTypeToDataTypeMap: Record<InputType, AiComponentDataType> = {
  [InputType.TEXT]: AiComponentDataType.S,
  [InputType.PASSWORD]: AiComponentDataType.S,
  [InputType.EMAIL]: AiComponentDataType.S,
  [InputType.NUMBER]: AiComponentDataType.N,
}

export type AiComponentIdentifier =
  | 'TX'
  | 'LK'
  | 'IN'
  | 'DT'
  | 'TA'
  | 'RD'
  | 'SL'
  | 'MS'
  | 'SW'
  | 'BT'
  | 'CK'
  | 'IM'
  | 'SD'
  | 'SBT'
  | 'FU'
  | 'MFU'
  | 'FP'
  | 'MSG'
  | 'TB'
  | 'TC'
  | 'CD'
  | 'FM'
  | 'CN'
  | 'CL'

const AiComponentIdentifierToComponentIdentifierEnum: Record<
  AiComponentIdentifier,
  ComponentIdentifierEnum | FormFieldsEnum
> = {
  TX: ComponentIdentifierEnum.TEXT,
  LK: ComponentIdentifierEnum.LINK,
  IN: FormFieldsEnum.INPUT,
  DT: FormFieldsEnum.DATEPICKER,
  TA: FormFieldsEnum.TEXTAREA,
  RD: FormFieldsEnum.RADIO,
  SL: FormFieldsEnum.SELECT,
  MS: FormFieldsEnum.MULTI_SELECT,
  SW: FormFieldsEnum.SWITCH,
  BT: ComponentIdentifierEnum.BUTTON,
  CK: FormFieldsEnum.CHECKBOX,
  IM: ComponentIdentifierEnum.IMAGE,
  SD: FormFieldsEnum.SLIDER,
  SBT: FormFieldsEnum.SEGMENTED_BUTTON,
  FU: ComponentIdentifierEnum.FILE_UPLOAD,
  MFU: ComponentIdentifierEnum.BULK_FILE_UPLOAD,
  FP: ComponentIdentifierEnum.DOCUMENT_PREVIEW,
  MSG: ComponentIdentifierEnum.MESSAGE,
  TB: ComponentIdentifierEnum.TABLE,
  TC: ComponentIdentifierEnum.TABLE_CELL,
  CD: ComponentIdentifierEnum.CARD,
  FM: ComponentIdentifierEnum.FORM,
  CN: ComponentIdentifierEnum.CONTAINER,
  CL: ComponentIdentifierEnum.COLLECTION,
}

export type AiUiTemplateElement = {
  id?: number
  ci: AiComponentIdentifier
  lb: string // label
  ex: string // example
  st: string // style
  k: string // key
  ds: string // description
  dt: string // data type
  gp: AiUiTemplateGridPosition
}

export type AiUiTemplateGridPosition = {
  cstart: number
  cspan: number
  rstart: number
  rspan: number
  al?: 'start' | 'center' | 'end'
  dir?: 'row' | 'column'
}

export type AiUiTemplateSectionSize = {
  lf: number
  up: number
  rg: number
  lo: number
}

enum TemplateConfigMergeCase {
  NO_DATA = 'no_data',
  EMPTY_UI = 'empty_ui',
  EMPTY_DRAFT = 'empty-draft',
  MERGE_TEMPLATES = 'merge-templates',
  INSIDE_CONTAINER = 'inside-container',
  EDIT_CURRENT = 'edit-current',
}

const extractTextStyle = (textStyle: string): string | null => {
  if (!textStyle) {
    return null
  }

  if (textStyle.split('/').length > 1) {
    if (['H1', 'H2', 'H3', 'H4', 'H5', 'H6'].indexOf(textStyle.split('/')[1]) > -1) {
      return 'typography@Heading/' + textStyle.split('/')[1] + '#' + textStyle.split('/')[0]
    } else if (['P1', 'P2', 'P3'].indexOf(textStyle.split('/')[1]) > -1) {
      return 'typography@Paragraph/' + textStyle.split('/')[1] + '#' + textStyle.split('/')[0]
    } else {
      return null
    }
  } else {
    return null
  }
}

const extractButtonType = (buttonStyle: string): ButtonVariant => {
  if (!buttonStyle) {
    return 'text'
  }

  if (buttonStyle === 'filled') {
    return 'fill'
  } else {
    return buttonStyle as ButtonVariant
  }
}

const parseDataSourceValues = (
  label: string
): Pick<NomenclatorValue, 'order' | 'label' | 'value'>[] =>
  label && label.search('//') > -1
    ? label.split('//').map((value, index) => ({ order: index, label: value, value }))
    : [{ order: 0, label, value: label || '' }]

const parceTableCellsValues = (label: string): string[] =>
  label?.split('//') || ['Column 1', 'Column 2', 'Column 3']

const parseSliderConfigValues = (label: string): { minValue: number; maxValue: number } => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [minValue, value, maxValue] = label?.split('/').map(Number) || [0, 0, 100]
  return { minValue, maxValue }
}

const parseGridAlignment = (alignment: AiUiTemplateGridPosition['al'] | null): GridPositionType => {
  switch (alignment) {
    case 'start':
      return 'start start'
    case 'center':
      return 'center center'
    case 'end':
      return 'end end'
    default:
      return 'start start'
  }
}

const getWidthTypeByAlignment = (alignment: AiUiTemplateGridPosition['al'] | null): WidthType => {
  if (alignment === 'center') {
    return DimensionTypes.Auto
  } else {
    return DimensionTypes.Fill
  }
}

const parseCollectionChildrenFromEx = (
  ex: string,
  parentKey: string,
  withIdsAndValidationStatus: WithIdsAndValidationStatus
): TemplateConfigInterface[] => {
  if (!ex || ex.indexOf('//') === -1) {
    return []
  }
  return ex.split('//').map((entry) => {
    const [typeCode, label] = entry.includes(':') ? entry.split(':', 2) : ['IN', entry]
    const componentIdentifier =
      AiComponentIdentifierToComponentIdentifierEnum[typeCode as AiComponentIdentifier] ||
      FormFieldsEnum.INPUT
    const childKey = label
      ? label
          .trim()
          .replace(/\s+(.)/g, (_, c) => c.toUpperCase())
          .replace(/^\w/, (c) => c.toLowerCase())
      : ''
    return _flow(
      withIdsAndValidationStatus,
      TemplateConfigEntity.withLabel(label?.trim() || ''),
      ...(childKey ? [TemplateConfigEntity.withKey(`${parentKey}.${childKey}`)] : [])
    )(
      TemplateConfigEntity.createDefaultTemplateConfig(componentIdentifier)
    ) as TemplateConfigInterface
  })
}

type WithIdsAndValidationStatus = (
  templateConfig: Partial<TemplateConfigInterface>
) => Partial<TemplateConfigInterface>

const mapAiElementToTemplateConfig = (
  withIdsAndValidationStatus: WithIdsAndValidationStatus,
  aiElement: AiUiTemplateElement
): TemplateConfigInterface => {
  const componentIdentifier = AiComponentIdentifierToComponentIdentifierEnum[aiElement.ci]
  const isButton = componentIdentifier === ComponentIdentifierEnum.BUTTON
  const isImage = componentIdentifier === ComponentIdentifierEnum.IMAGE
  const isText = componentIdentifier === ComponentIdentifierEnum.TEXT
  const isLink = componentIdentifier === ComponentIdentifierEnum.LINK
  const isMessage = componentIdentifier === ComponentIdentifierEnum.MESSAGE
  const isTable = componentIdentifier === ComponentIdentifierEnum.TABLE
  const hasLabel = !isImage

  const textStyle = extractTextStyle(aiElement.st)
  const buttonType = extractButtonType(aiElement.st)
  const hasLabelStyle = (isText || isLink) && !!textStyle

  const position = parseGridAlignment(aiElement.gp?.al || 'start')

  const templateConfigBuildSteps = [
    withIdsAndValidationStatus,
    TemplateConfigEntity.withGridChildLayout(Platform.Web, {
      colSpan: aiElement.gp?.cspan || 1,
      rowSpan: aiElement.gp?.rspan || 1,
      position,
    }),
    TemplateConfigEntity.withWidthType(
      Platform.Web,
      getWidthTypeByAlignment(aiElement.gp?.al || 'start'),
      undefined
    ),
    ...(hasLabel
      ? [TemplateConfigEntity.withLabel(aiElement.lb === '' ? aiElement.ex : aiElement.lb)]
      : []),
    ...(hasLabelStyle ? [TemplateConfigEntity.withLabelStyle(Platform.Web, textStyle)] : []),
    ...(isButton ? [TemplateConfigEntity.withButtonType(buttonType)] : []),
    ...(isImage
      ? [
          TemplateConfigEntity.withFixedSize(Platform.Web, {
            width: parseInt(aiElement.st?.split('x')[0], 10),
            height: parseInt(aiElement.st?.split('x')[1], 10),
          }),
          TemplateConfigEntity.withImageUrlSource(''),
        ]
      : []),
    ...(isMessage ? [TemplateConfigEntity.withMessageType(aiElement.st as MessageType)] : []),
  ]

  const component = _flow(templateConfigBuildSteps)(
    TemplateConfigEntity.createDefaultTemplateConfig(componentIdentifier)
  )
  if (isTable) {
    let rowComponent = _flow(withIdsAndValidationStatus)(
      TemplateConfigEntity.createDefaultTemplateConfig(ComponentIdentifierEnum.TABLE_ROW)
    )
    const cellValueComponents = parceTableCellsValues(aiElement.ex)
    const cellValueLabels = parceTableCellsValues(aiElement.st)
    // Use temporary array instead of pushing directly into a frozen object.
    const newTemplateConfig = []
    parceTableCellsValues(aiElement.lb).forEach((cellValue, index) => {
      // ...existing code to build cellComponent...
      const cellComponent = _flow([
        withIdsAndValidationStatus,
        TemplateConfigEntity.withLabel(cellValue),
      ])(TemplateConfigEntity.createDefaultTemplateConfig(ComponentIdentifierEnum.TABLE_CELL))
      const cellComponentIdentifier =
        AiComponentIdentifierToComponentIdentifierEnum[cellValueComponents[index]]
      const cellValueComponent = _flow([
        withIdsAndValidationStatus,
        ...(cellComponentIdentifier === ComponentIdentifierEnum.IMAGE
          ? [
              TemplateConfigEntity.withFixedSize(Platform.Web, {
                width: 48,
                height: 48,
              }),
              TemplateConfigEntity.withImageUrlSource(''),
            ]
          : []),
        ...(cellComponentIdentifier === ComponentIdentifierEnum.BUTTON
          ? [TemplateConfigEntity.withLabel(cellValue)]
          : [TemplateConfigEntity.withLabel('${' + cellValueLabels[index] + '}')]),
      ])(TemplateConfigEntity.createDefaultTemplateConfig(cellComponentIdentifier))

      cellComponent.templateConfig = [cellValueComponent]
      newTemplateConfig.push(cellComponent)
    })
    rowComponent = { ...rowComponent, templateConfig: newTemplateConfig } // assign new array
    component.templateConfig = [rowComponent]
  }

  if (componentIdentifier === ComponentIdentifierEnum.COLLECTION) {
    const prototypeComponent = _flow(withIdsAndValidationStatus)(
      TemplateConfigEntity.createDefaultTemplateConfig(ComponentIdentifierEnum.COLLECTION_PROTOTYPE)
    )
    const collectionChildren = parseCollectionChildrenFromEx(
      aiElement.ex,
      aiElement.k,
      withIdsAndValidationStatus
    )
    component.templateConfig = [{ ...prototypeComponent, templateConfig: collectionChildren }]
  }

  return component
}

const mapAiElementToFormField = (
  withIdsAndValidationStatus: WithIdsAndValidationStatus,
  parentKey: string,
  aiElement: AiUiTemplateElement
): Partial<TemplateConfigInterface> => {
  const elementsWithDataSource = new Set<FormFieldsEnum>([
    FormFieldsEnum.RADIO,
    FormFieldsEnum.SELECT,
    FormFieldsEnum.MULTI_SELECT,
    FormFieldsEnum.CHECKBOX,
    FormFieldsEnum.SEGMENTED_BUTTON,
  ])

  const componentIdentifier = AiComponentIdentifierToComponentIdentifierEnum[aiElement.ci]
  const dataSourceValues = parseDataSourceValues(aiElement.ex)

  const hasDataSource =
    elementsWithDataSource.has(componentIdentifier as FormFieldsEnum) && dataSourceValues.length > 0
  const isInput = componentIdentifier === FormFieldsEnum.INPUT
  const isSlider = componentIdentifier === FormFieldsEnum.SLIDER

  const formElementBuildSteps = [
    withIdsAndValidationStatus,
    TemplateConfigEntity.withGridChildLayout(Platform.Web, {
      colSpan: aiElement.gp?.cspan || 1,
      rowSpan: aiElement.gp?.rspan || 1,
    }),
    TemplateConfigEntity.withWidthType(Platform.Web, DimensionTypes.Fill, undefined),
    TemplateConfigEntity.withKey(`${parentKey}.${aiElement.k}`),
    ...(isInput && aiElement.ex
      ? [TemplateConfigEntity.withPlaceholder(aiElement.ex)]
      : aiElement.ds
        ? [TemplateConfigEntity.withPlaceholder(aiElement.ds)]
        : []),
    // TODO: withHelper not yet available in TemplateConfigEntity
    TemplateConfigEntity.withLabel(aiElement.lb),
    ...(hasDataSource ? [TemplateConfigEntity.withStaticDataSource(dataSourceValues)] : []),
    ...(isInput
      ? [
          TemplateConfigEntity.withInputType(
            aiElement.st as DisplayOptionsInterface['flowxProps']['inputType']
          ),
        ]
      : []),
    ...(isSlider
      ? [TemplateConfigEntity.withSliderConfig(parseSliderConfigValues(aiElement.ex))]
      : []),
  ]

  return _flow(formElementBuildSteps)(
    TemplateConfigEntity.createDefaultTemplateConfig(componentIdentifier)
  )
}

const unknownComponentPlaceholder = (
  aiElement: AiUiTemplateElement
): Partial<TemplateConfigInterface> =>
  _flow(
    TemplateConfigEntity.withLabel('Unknown component PLACEHOLDER'),
    TemplateConfigEntity.withGridChildLayout(Platform.Web, {
      colSpan: aiElement.gp?.cspan || 1,
      rowSpan: aiElement.gp?.rspan || 1,
    })
  )(TemplateConfigEntity.createDefaultTemplateConfig(ComponentIdentifierEnum.TEXT))

const decorateTemplateConfigTreeWithOrder = (
  order = 0,
  templateConfig: TemplateConfigInterface
): TemplateConfigInterface => ({
  ...templateConfig,
  order,
  ...(templateConfig.templateConfig
    ? {
        templateConfig: templateConfig.templateConfig.map((child, index) =>
          decorateTemplateConfigTreeWithOrder(index, child)
        ),
      }
    : {}),
})

export const mapAiUiToTemplateConfig = (
  idGenerator: () => number,
  flowxUuidGenerator: () => string,
  aiTemplateConfig: AiTemplateConfig
): Partial<TemplateConfigInterface> => {
  const withIdsAndValidationStatus: WithIdsAndValidationStatus = _flow(
    TemplateConfigEntity.withId(idGenerator()),
    TemplateConfigEntity.withFlowxUuid(flowxUuidGenerator),
    TemplateConfigEntity.withMeta({
      validationStatus: TemplateConfigValidationStatus.VALID,
      saveStatus: TemplateConfigSaveStatus.DRAFT,
    })
  )

  const interfaceTC: Partial<TemplateConfigInterface> = _flow(
    withIdsAndValidationStatus,
    TemplateConfigEntity.withGridLayout(Platform.Web, {
      columns: aiTemplateConfig?.interface?.clno || 1,
      columnWidths: Array.from({ length: aiTemplateConfig?.interface?.clno || 1 }, () => ({
        width: 1,
        unit: 'fr',
      })),
      columnGap: 8,
      rowGap: 8,
    })
  )(TemplateConfigEntity.createDefaultTemplateConfig(ComponentIdentifierEnum.CONTAINER))

  const interfaceWithSections = produce(interfaceTC, (interfaceTCDraft) => {
    aiTemplateConfig.sectionsDetails?.forEach((aiSection) => {
      const regionTCs = aiSection.rs?.map((aiRegion) => {
        const rows = [[]]

        let columnSpan = 0

        aiRegion.cs?.forEach((aiElement) => {
          const wantsOnTheSameRow = aiElement.gp?.cstart > columnSpan
          const hasSpaceOnTheSameRow = columnSpan + aiElement.gp?.cspan <= aiRegion.clno
          if (wantsOnTheSameRow && hasSpaceOnTheSameRow) {
            columnSpan += aiElement.gp?.cspan
            rows[rows.length - 1].push(aiElement)
          } else {
            columnSpan = aiElement.gp?.cspan
            rows.push([aiElement])
          }
        })

        const aiFormElements = new Set<AiComponentIdentifier>([
          'IN',
          'DT',
          'TA',
          'RD',
          'SL',
          'MS',
          'SW',
          'CK',
          'SD',
          'SBT',
        ])

        let componentStartIndexInRegion = 0
        const formFieldsZones = rows.map((row) => {
          let start = -1
          let startIndex = -1

          const rowInfo: {
            startIndex: number
            endIndex: number
            start: number
            span: number
          }[] = []

          row.forEach((aiElement, index) => {
            if (aiFormElements.has(aiElement.ci)) {
              if (index === row.length - 1) {
                rowInfo.push({
                  start: start === -1 ? aiElement.gp?.cstart || 1 : start,
                  span:
                    start === -1
                      ? aiElement.gp?.cspan || 1
                      : aiElement.gp?.cstart || 1 - start + aiElement.gp?.cspan || 1,
                  startIndex: start === -1 ? componentStartIndexInRegion + index : startIndex,
                  endIndex: componentStartIndexInRegion + index,
                })
              }
              if (start === -1) {
                startIndex = componentStartIndexInRegion + index
                start = aiElement.gp?.cstart || 1
              }
            } else {
              if (start !== -1) {
                const span = aiElement.gp?.cstart || 1 - start
                rowInfo.push({
                  start,
                  span,
                  startIndex,
                  endIndex: componentStartIndexInRegion + index - 1,
                })
                start = -1
                startIndex = -1
              }
            }
          })

          componentStartIndexInRegion += row.length

          return rowInfo
        }) satisfies {
          startIndex: number
          endIndex: number
          start: number
          span: number
        }[][]

        const formFieldsGroupedInfo: {
          startIndex: number
          endIndex: number
          start: number
          span: number
        }[] = []

        let startRow = -1
        let startIndex = -1
        formFieldsZones.forEach((row, rowIndex) => {
          if (row.length > 0) {
            const rowHasASingleForm = row.length === 1
            const formIsNotOffseted = row[0].start === 1
            const rowContainsOnlyFormFields = rows[rowIndex].every((aiElement) =>
              aiFormElements.has(aiElement.ci)
            )

            if (rowHasASingleForm && formIsNotOffseted && rowContainsOnlyFormFields) {
              if (startRow === -1) {
                startRow = rowIndex
                startIndex = row[0].startIndex
              }
            } else if (startRow !== -1) {
              const endRow = rowIndex - 1
              const endIndex = formFieldsZones[endRow][0].endIndex
              formFieldsGroupedInfo.push({
                startIndex,
                endIndex,
                start: 1,
                span: aiRegion.clno,
              })
              formFieldsGroupedInfo.push(...row)
              startRow = -1
              startIndex = -1
            } else {
              formFieldsGroupedInfo.push(...row)
            }
          } else {
            if (startRow !== -1) {
              const endRow = rowIndex - 1
              const endIndex = formFieldsZones[endRow][0].endIndex
              formFieldsGroupedInfo.push({
                startIndex,
                endIndex,
                start: 1,
                span: aiRegion.clno,
              })
              startRow = -1
              startIndex = -1
            }
          }

          if (startRow !== -1 && rowIndex === formFieldsZones.length - 1) {
            const endIndex = _last(formFieldsZones.filter((zone) => zone.length > 0))[0].endIndex
            // const endIndex = formFieldsZones[endRow][0].end
            formFieldsGroupedInfo.push({
              startIndex,
              endIndex,
              start: 1,
              span: aiRegion.clno,
            })
          }
        })

        const formsInfo: {
          startIndex: number
          endIndex: number
          formTC: Partial<TemplateConfigInterface>
        }[] = []

        formFieldsGroupedInfo.forEach((form) => {
          formsInfo.push({
            startIndex: form.startIndex,
            endIndex: form.endIndex,
            formTC: _flow(
              withIdsAndValidationStatus,
              TemplateConfigEntity.withLabel(''),
              TemplateConfigEntity.withGridLayout(Platform.Web, {
                columns: form.span,
                columnWidths: Array.from({ length: form.span }, () => ({
                  width: 1,
                  unit: 'fr',
                })),
                columnGap: 8,
                rowGap: 8,
              }),
              TemplateConfigEntity.withGridChildLayout(Platform.Web, {
                colSpan: form.span,
              })
            )(TemplateConfigEntity.createDefaultTemplateConfig(ComponentIdentifierEnum.FORM)),
          })
        })

        const componentsTCs = aiRegion.cs?.flatMap((aiElement, index) => {
          if (aiElement.ci) {
            const templateConfigIdentifier =
              AiComponentIdentifierToComponentIdentifierEnum[aiElement.ci]

            const isFormField = allowedFormComponents.includes(
              templateConfigIdentifier as FormFieldsEnum
            )

            if (!templateConfigIdentifier) {
              console.error(
                `Unknown component identifier: "${aiElement.ci}". An empty container will be used instead`
              )
            }

            const componentTC = templateConfigIdentifier
              ? isFormField
                ? mapAiElementToFormField(withIdsAndValidationStatus, aiSection.sc.k, aiElement)
                : mapAiElementToTemplateConfig(withIdsAndValidationStatus, aiElement)
              : unknownComponentPlaceholder(aiElement)

            const previousComponent = aiRegion.cs[index - 1]

            const paddingColSpan = previousComponent
              ? aiElement.gp?.cstart - (previousComponent.gp?.cstart + previousComponent.gp?.cspan)
              : aiElement.gp?.cstart - 1

            const shouldAddPadding = paddingColSpan > 0 && !isFormField

            const paddingContainerTC: Partial<TemplateConfigInterface>[] = shouldAddPadding
              ? [
                  _flow(
                    withIdsAndValidationStatus,
                    TemplateConfigEntity.withGridChildLayout(Platform.Web, {
                      colSpan: paddingColSpan,
                    })
                  )(
                    TemplateConfigEntity.createDefaultTemplateConfig(
                      ComponentIdentifierEnum.CONTAINER
                    )
                  ),
                ]
              : []

            return [...paddingContainerTC, componentTC]
          } else {
            return []
          }
        })

        const componentsWithFormsTCs: Partial<TemplateConfigInterface>[] = []
        if (componentsTCs) {
          for (let index = 0; index < componentsTCs.length; index++) {
            const componentTC = componentsTCs[index]

            const formGroupIndex = formsInfo.findIndex(
              (form) => form.startIndex <= index && form.endIndex >= index
            )

            if (formGroupIndex === -1) {
              componentsWithFormsTCs.push(componentTC)
            } else {
              const formGroup = formsInfo[formGroupIndex]
              const formFields = componentsTCs.slice(formGroup.startIndex, formGroup.endIndex + 1)
              componentsWithFormsTCs.push(
                TemplateConfigEntity.addChildren(formFields, formGroup.formTC)
              )
              index = formGroup.endIndex
            }
          }
        }
        // const firstChild = aiRegion.cs ? aiRegion.cs[0] : null

        const cardTitle = null
        // aiRegion.tp === 'CD' && firstChild
        //   ? isTextHeading(firstChild) && isLeftAligned(firstChild)
        //     ? firstChild.lb
        //     : null
        //   : null

        const componentsWithFormsTCsWithoutCardTitle = cardTitle
          ? componentsWithFormsTCs.slice(1)
          : componentsWithFormsTCs

        const regionTemplateConfigBuildSteps = [
          withIdsAndValidationStatus,
          TemplateConfigEntity.withGridLayout(Platform.Web, {
            columns: aiRegion.clno || 1,
            columnWidths: Array.from({ length: aiRegion.clno || 1 }, () => ({
              width: 1,
              unit: 'fr',
            })),
            columnGap: 8,
            rowGap: 8,
          }),
          TemplateConfigEntity.withGridChildLayout(Platform.Web, {
            colSpan: aiRegion.clno,
            rowSpan: aiRegion.rspan || 1,
          }),
          ...(cardTitle ? [TemplateConfigEntity.withCardTitle(Platform.Web, cardTitle, '')] : []),
          TemplateConfigEntity.addChildren(componentsWithFormsTCsWithoutCardTitle),
        ]

        return aiRegion.tp
          ? _flow(regionTemplateConfigBuildSteps)(
              TemplateConfigEntity.createDefaultTemplateConfig(
                AiRegionTypeToComponentIdentifierEnum[aiRegion.tp]
              )
            )
          : null
      })

      const sectionTC = _flow(
        withIdsAndValidationStatus,
        TemplateConfigEntity.withGridLayout(Platform.Web, {
          columns: aiSection.sc?.clno || 1,
          columnWidths: Array.from({ length: aiSection.sc?.clno || 1 }, () => ({
            width: 1,
            unit: 'fr',
          })),
          columnGap: 8,
          rowGap: 8,
        }),
        TemplateConfigEntity.withGridChildLayout(Platform.Web, {
          colSpan: aiSection.sc?.gp?.cspan || 1,
          rowSpan: aiSection.sc?.gp?.rspan || 1,
          position: parseGridAlignment(aiSection.sc?.gp?.al || 'start'),
        }),
        ...(regionTCs ? [TemplateConfigEntity.addChildren(regionTCs)] : [])
      )(TemplateConfigEntity.createDefaultTemplateConfig(ComponentIdentifierEnum.CONTAINER))

      if (regionTCs && regionTCs.length === 1) {
        const sectionGridChild = TemplateConfigEntity.getGridChildLayout(Platform.Web, sectionTC)
        if (regionTCs && regionTCs[0]) {
          const regionTCWithSectionGridChildLayout = _flow(
            TemplateConfigEntity.withGridChildLayout(Platform.Web, sectionGridChild)
          )(regionTCs[0] as TemplateConfigInterface)

          interfaceTCDraft.templateConfig.push(
            regionTCWithSectionGridChildLayout as TemplateConfigInterface
          )
        }
      } else {
        interfaceTCDraft.templateConfig.push(sectionTC as TemplateConfigInterface)
      }
    })
  })

  const templateConfigToReturn =
    interfaceWithSections.templateConfig.length === 1
      ? interfaceWithSections.templateConfig[0]
      : interfaceWithSections
  return decorateTemplateConfigTreeWithOrder(0, templateConfigToReturn as TemplateConfigInterface)
}

const searchAndRemove = (data: any, keys: string[]): void => {
  if (Array.isArray(data)) {
    data.forEach((item) => searchAndRemove(item, keys))
    return
  }
  if (typeof data !== 'object' || data === null) {
    return
  }

  // Always check and delete at this level
  if (Object.prototype.hasOwnProperty.call(data, keys[0])) {
    if (keys.length === 1) {
      delete data[keys[0]]
    } else {
      searchAndRemove(data[keys[0]], keys.slice(1))
    }
  }
  // Recurse into all children (but don't repeat root-level delete)
  Object.values(data).forEach((value) => {
    if (typeof value === 'object' && value !== null) {
      searchAndRemove(value, keys)
    }
  })
}

const removeArrayElementsByField = (
  data: any,
  arrayPath: string[],
  field: string,
  value: string
): void => {
  if (Array.isArray(data)) {
    data.forEach((item) => removeArrayElementsByField(item, arrayPath, field, value))
    return
  }
  if (typeof data !== 'object' || data === null) {
    return
  }

  if (arrayPath.length === 1 && (data as any).hasOwnProperty(arrayPath[0])) {
    const arr = (data as any)[arrayPath[0]]
    if (Array.isArray(arr)) {
      (data as any)[arrayPath[0]] = arr.filter((item: any) => item[field] !== value)
    }
  } else if (arrayPath.length > 1 && (data as any).hasOwnProperty(arrayPath[0])) {
    removeArrayElementsByField((data as any)[arrayPath[0]], arrayPath.slice(1), field, value)
  }
  Object.values(data as any).forEach((value_) =>
    removeArrayElementsByField(value_, arrayPath, field, value)
  )
}

/**
 * Removes specified fields from a JSON object, supporting dot-separated paths, wildcards, and array element removal by field value.
 * @param data The JSON object to process (will be mutated).
 * @param fieldsToRemove List of dot-separated field paths to remove. Use "*." as a wildcard. Use "*.arrayName.field='value'" to remove array elements by field value.
 * @returns The modified JSON object.
 */
const removeFieldsFromJson = <T extends { [key: string]: any }>(
  data: any,
  fieldsToRemove: string[]
): T => {
  // Deep clone to avoid mutating frozen or shared objects
  const clone = JSON.parse(JSON.stringify(data))
  for (const field of fieldsToRemove) {
    // Check for array filter syntax: *.arrayName.field='value'
    const match = field.match(/^(\.\*)?([\w.]+)\.([\w]+)='([^']+)'$/)
    if (match) {
      // e.g. "*.platformDisplayOptions.platform='android'"
      const arrayPath = match[2].split('.')
      const fieldName = match[3]
      const value = match[4]
      removeArrayElementsByField(clone, arrayPath, fieldName, value)
      continue
    }
    // ...existing logic for normal field removal...
    const keys = field.split('.')
    // Direct root-level delete for single-key fields
    if (keys.length === 1) {
      delete clone[field]
    }
    searchAndRemove(clone, keys)
  }
  return clone
}

const prepareUIContextForAI = (uiContext: any): any =>
  removeFieldsFromJson(uiContext, [
    'flowxUuid',
    'meta',
    "platformsDisplayOptions.platform='android'",
    "platformsDisplayOptions.platform='web'",
    "platformsDisplayOptions.platform='ios'",
    'platformsDisplayOptions.className',
    'platformsDisplayOptions.formFieldId',
    'platformsDisplayOptions.flowxProps.dataTestId',
    '*.flowxUuid',
    '*.meta',
    '*.dataSource',
    "*.platformsDisplayOptions.platform='android'",
    "*.platformsDisplayOptions.platform='web'",
    "*.platformsDisplayOptions.platform='ios'",
    '*.platformsDisplayOptions.className',
    '*.platformsDisplayOptions.formFieldId',
    '*.platformsDisplayOptions.flowxProps.dataTestId',
  ])

const mergeDraftTemplateConfig = (
  draftTemplateConfig: TemplateConfigInterface,
  templateConfig: TemplateConfigInterface,
  selectedTemplateConfigId?: number,
  generatedLocationId?: AiLocationUI
): TemplateConfigInterface => {
  const hasTemplateConfig = !_isEmpty(templateConfig)
  const hasDraftTemplateConfig = !_isEmpty(draftTemplateConfig)
  const hasLocationId = !_isEmpty(generatedLocationId)

  let mergeCase: TemplateConfigMergeCase | null = null
  if (hasLocationId && hasTemplateConfig) {
    mergeCase = TemplateConfigMergeCase.EDIT_CURRENT
  } else if (!hasTemplateConfig && !hasDraftTemplateConfig) {
    mergeCase = TemplateConfigMergeCase.NO_DATA
  } else if (!hasTemplateConfig && hasDraftTemplateConfig) {
    mergeCase = TemplateConfigMergeCase.EMPTY_UI
  } else if (hasTemplateConfig && !hasDraftTemplateConfig) {
    mergeCase = TemplateConfigMergeCase.EMPTY_DRAFT
  } else {
    if (selectedTemplateConfigId && templateConfig.id !== selectedTemplateConfigId) {
      mergeCase = TemplateConfigMergeCase.INSIDE_CONTAINER
    } else {
      mergeCase = TemplateConfigMergeCase.MERGE_TEMPLATES
    }
  }

  switch (mergeCase) {
    case TemplateConfigMergeCase.NO_DATA: {
      return null
    }
    case TemplateConfigMergeCase.EMPTY_UI: {
      return { ...draftTemplateConfig, draft: true }
    }
    case TemplateConfigMergeCase.EMPTY_DRAFT: {
      return templateConfig
    }
    case TemplateConfigMergeCase.INSIDE_CONTAINER: {
      if (templateConfig.id === selectedTemplateConfigId) {
        return {
          ...templateConfig,
          templateConfig: [{ ...draftTemplateConfig, draft: true, order: 0 }],
        }
      } else {
        if (templateConfig.templateConfig?.length) {
          return produce(templateConfig, (draft) => {
            draft.templateConfig = draft.templateConfig.map((child) =>
              mergeDraftTemplateConfig(
                draftTemplateConfig,
                child,
                selectedTemplateConfigId,
                generatedLocationId
              )
            )
          })
        } else {
          return templateConfig
        }
      }
    }
    case TemplateConfigMergeCase.EDIT_CURRENT: {
      if (templateConfig.id === generatedLocationId?.ci) {
        return {
          ...templateConfig,
          templateConfig: [{ ...draftTemplateConfig, draft: true, order: 0 }],
        }
      } else {
        if (templateConfig.templateConfig?.length) {
          return produce(templateConfig, (draft) => {
            draft.templateConfig = draft.templateConfig.map((child) =>
              mergeDraftTemplateConfig(
                draftTemplateConfig,
                child,
                selectedTemplateConfigId,
                generatedLocationId
              )
            )
          })
        } else {
          return templateConfig
        }
      }
      const lastChildOrder = _last(templateConfig.templateConfig)?.order
      return {
        ...templateConfig,
        templateConfig: [
          ...templateConfig.templateConfig,
          {
            ...draftTemplateConfig,
            draft: true,
            order: lastChildOrder != null ? lastChildOrder + 1 : 0,
          },
        ],
      }
    }
    case TemplateConfigMergeCase.MERGE_TEMPLATES: {
      const lastChildOrder = _last(templateConfig.templateConfig)?.order
      return {
        ...templateConfig,
        templateConfig: [
          ...templateConfig.templateConfig,
          {
            ...draftTemplateConfig,
            draft: true,
            order: lastChildOrder != null ? lastChildOrder + 1 : 0,
          },
        ],
      }
    }
  }
}

const getTemplateConfigMergedDisplayOptions = (
  config: Partial<TemplateConfigInterface>
): DisplayOptionsInterface =>
  config.platformsDisplayOptions.reduce(
    (acc, platformOptions) => deepmerge(acc, platformOptions),
    {} as any
  )

const parseTemplateToBulkUpdate = (
  config: TemplateConfigInterface,
  aiContext: AiDesignerContext | Record<string, never> = {}
): BulkUpdatePayload => {
  const { cs: aiFields } = JSON.parse(aiContext.answer ?? null) ?? {}
  const { key, templateConfig, id, ...validConfigProps } = config

  const templateConfigWithData =
    templateConfig?.map((config) => parseTemplateToBulkUpdate(config, aiContext)) ?? []

  return {
    ...validConfigProps,
    key,
    templateConfigWithData,
    dataModelProperty: !_isEmpty(key)
      ? {
          name: key,
          description: aiFields?.find((field) => field.k === key)?.ds,
          type: UiDesignerService.getFormFieldDataModelType(
            validConfigProps.componentIdentifier,
            getTemplateConfigMergedDisplayOptions(validConfigProps)?.flowxProps.inputType
          ),
          exampleValue: aiFields?.find((field) => field.k === key)?.ex,
          useInReporting: false,
          isSensitive: false,
          isArray: false,
        }
      : null,
  }
}

const findInMergedConfig = (
  config: TemplateConfigInterface,
  aiDraftId: string
): TemplateConfigInterface => {
  const isDraftFound = config.meta?.aiDraftId === aiDraftId

  if (isDraftFound) {
    return config
  } else if (config.templateConfig?.length) {
    return config.templateConfig.find((child) => findInMergedConfig(child, aiDraftId))
  }

  return null
}

const toBulkUpdatePayload = (payload: {
  draftConfig: TemplateConfigInterface[]
  mergedConfig: TemplateConfigInterface
  aiContext?: AiDesignerContext
}): BulkUpdatePayload[] => {
  const { draftConfig, mergedConfig, aiContext } = payload

  return draftConfig.map((config) =>
    parseTemplateToBulkUpdate(
      deepmerge(config, {
        order: findInMergedConfig(mergedConfig, config.meta?.aiDraftId)?.order,
      } as any),
      aiContext
    )
  )
}

const extractDataModelItemsFromAiConfig = (
  config: AiTemplateConfig,
  mappedFields: AiDesignerContext['mapped_fields']
): DataModelDraftItem[] => {
  const extractKeysFn = (
    prefix: string,
    config: AiUiTemplateElement
  ): DataModelDraftItem | DataModelDraftItem[] => {
    const newPrefix = config.k ? `${prefix}${prefix ? '.' : ''}${config.k}` : prefix
    const inputDataType =
      AiComponentIdentifierToComponentIdentifierEnum[config.ci] === FormFieldsEnum.INPUT
        ? AiInputTypeToDataTypeMap[config.st]
        : null
    const type =
      inputDataType ?? (AiComponentDataType[config.dt] as unknown as DataModelAttributeType)

    const mappedField = mappedFields.find((field) => field.key === newPrefix)

    const createMappedDataModelDraftItem = (
      config: AiUiTemplateElement,
      mappedField: AiDesignerContext['mapped_fields'][0]
    ): DataModelDraftItem =>
      ({
        label: config.lb,
        templateConfigIdentifier: AiComponentIdentifierToComponentIdentifierEnum[config.ci],
        key: mappedField.model,
        description: mappedField.dataModelEntity.description,
        type: mappedField.dataModelEntity.type,
      }) as DataModelDraftItem

    const createNewDataModelDraftItem = (config: AiUiTemplateElement): DataModelDraftItem =>
      ({
        label: config.lb,
        key: newPrefix,
        description: config.ds,
        ...(config.dt === 'A'
          ? { type: DataModelAttributeType.STRING, multiInstance: true }
          : {
              type: config.dt === AiComponentDataType.NONE ? DataModelAttributeType.STRING : type,
              multiInstance: false,
            }),
        templateConfigIdentifier: AiComponentIdentifierToComponentIdentifierEnum[config.ci],
      }) as DataModelDraftItem

    return mappedField
      ? createMappedDataModelDraftItem(config, mappedField)
      : createNewDataModelDraftItem(config)
  }

  return _flatten(
    config.sectionsDetails.map((section: AiUiTemplateSectionDetails) =>
      _flatten(
        section.rs.map((region: AiUiTemplateRegion) =>
          _flatten(
            region.cs
              .filter((config: AiUiTemplateElement) =>
                allowedFormComponents.includes(
                  AiComponentIdentifierToComponentIdentifierEnum[config.ci] as FormFieldsEnum
                )
              )
              .map((config: AiUiTemplateElement) => extractKeysFn(section.sc.k, config))
          )
        )
      )
    )
  )
}

const extractDataModelChangesFromAiConfig = (
  config: AiTemplateConfig,
  mappedFields: AiDesignerContext['mapped_fields']
): DataModelDraft | null => {
  const changes = extractDataModelItemsFromAiConfig(config, mappedFields).reduce(
    (filteredDataModels, dataModel) => {
      const mappedField = mappedFields.find((mappedField) => dataModel.key === mappedField.model)

      if (mappedField) {
        filteredDataModels.existing.push({
          ...dataModel,
          templateConfigMappedKey: mappedField.key,
        })
      } else {
        filteredDataModels.new.push(dataModel)
      }

      return filteredDataModels
    },
    { new: [] as DataModelDraftItem[], existing: [] as DataModelDraftItem[] }
  )
  return changes.new.length || changes.existing.length ? changes : null
}

const extractDataModelUpdateFromTemplateConfig = (
  config: TemplateConfigInterface | TemplateConfigInterface[]
): DataModelDraftUpdateItem[] => {
  if (Array.isArray(config)) {
    return _flatten(config.map((child) => extractDataModelUpdateFromTemplateConfig(child)))
  }

  const { id, key, componentIdentifier, templateConfig } = config

  return _flattenDeep([
    key ? ({ key, id, componentIdentifier } satisfies DataModelDraftUpdateItem) : null,
    templateConfig?.length
      ? templateConfig.map((child) => extractDataModelUpdateFromTemplateConfig(child))
      : null,
  ]).filter(Boolean)
}

const updateDataModelWithTemplateConfigIdentifiers = (
  dataModelChanges: DataModelDraft | null,
  templateConfig: TemplateConfigInterface | TemplateConfigInterface[]
): DataModelDraft => {
  const updates = extractDataModelUpdateFromTemplateConfig(templateConfig)

  function updateDataModelItem(dataModel: DataModelDraftItem): DataModelDraftItem {
    const update = updates.find(
      (update) => update.key === dataModel.key || update.key === dataModel.templateConfigMappedKey
    )
    return update
      ? {
          ...dataModel,
          templateConfigIdentifier: update.componentIdentifier,
          templateConfigId: update.id,
        }
      : dataModel
  }

  return {
    new: dataModelChanges.new.map(updateDataModelItem),
    existing: dataModelChanges.existing.map(updateDataModelItem),
  }
}

export const DesignerService = {
  AiComponentIdentifierToComponentIdentifierEnum,
  mapAiUiToTemplateConfig: _curry(mapAiUiToTemplateConfig),
  mergeDraftTemplateConfig,
  toBulkUpdatePayload,
  extractDataModelChangesFromAiConfig,
  updateDataModelWithTemplateConfigIdentifiers,
  prepareUIContextForAI,
}
